import type { Unit } from './sqft.js';

// ─── Core enums ──────────────────────────────────────────────────────────────

export type Role = 'admin' | 'operator';
export type UserStatus = 'active' | 'inactive';
export type EstimateStatus = 'draft' | 'issued' | 'partial' | 'paid' | 'returned';
export type POStatus = 'draft' | 'sent' | 'received' | 'returned' | 'cancelled';
export type CountertopJobStatus =
  | 'inquiry'
  | 'quoted'
  | 'advance_paid'
  | 'measured'
  | 'ordered'
  | 'installing'
  | 'installed'
  | 'cancelled';
export type ReturnStatus = 'open' | 'refunded';
export type ItemCategory = 'goods' | 'service';
export type DiscountType = 'pct' | 'fixed';
export type StockDirection = 'increase' | 'decrease';
export type StockReason = 'damage' | 'loss' | 'theft' | 'recount' | 'other';
export type EntityType = 'customer' | 'supplier';
export type CreditSourceType =
  | 'sales_return'
  | 'purchase_return'
  | 'payment_applied'
  | 'adjustment';
export type AttachmentModule =
  | 'estimate'
  | 'purchase_order'
  | 'sales_return'
  | 'purchase_return'
  | 'customer'
  | 'supplier'
  | 'expense'
  | 'countertop_job';

// ─── API shapes ───────────────────────────────────────────────────────────────

export interface ApiResponse<T = unknown> {
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export interface SessionUser {
  id: number;
  username: string;
  full_name: string;
  role: Role;
  status: UserStatus;
  force_password_change: boolean;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  user: SessionUser;
}

// ─── Users ────────────────────────────────────────────────────────────────────

export interface User {
  id: number;
  username: string;
  full_name: string;
  email: string | null;
  role: Role;
  status: UserStatus;
  force_password_change: boolean;
  created_at: string;
}

export interface CreateUserRequest {
  username: string;
  full_name: string;
  email?: string;
  password: string;
  role: Role;
}

export interface UpdateUserRequest {
  full_name?: string;
  email?: string;
  role?: Role;
  status?: UserStatus;
  password?: string;
  force_password_change?: boolean;
}

// ─── Customers ────────────────────────────────────────────────────────────────

export interface Customer {
  id: number;
  code: string | null;
  name: string;
  contact_person: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  status: UserStatus;
  credit_limit: number; // cents
  created_at: string;
}

export interface CreateCustomerRequest {
  code?: string;
  name: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  address?: string;
  credit_limit?: number;
}

// ─── Suppliers ────────────────────────────────────────────────────────────────

export interface Supplier {
  id: number;
  code: string | null;
  name: string;
  contact_person: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  status: UserStatus;
  created_at: string;
  outstanding_balance: number;
}

export interface CreateSupplierRequest {
  code?: string;
  name: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  address?: string;
}

// ─── Inventory ────────────────────────────────────────────────────────────────

export interface InventoryItem {
  id: number;
  code: string | null;
  name: string;
  category: ItemCategory;
  unit: Unit;
  selling_price: number; // cents
  purchase_price: number; // cents
  stock_qty: number;
  min_stock: number;
  sqft_per_box: number | null;
  sqft_per_piece: number | null;
  pack_qty: number | null;
  pack_unit: string | null;
  weight: number | null;
  dimensions: string | null;
  image_path: string | null;
  status: UserStatus;
  created_at: string;
  preferred_supplier_id: number | null;
  preferred_supplier_name: string | null;
}

export interface CreateInventoryItemRequest {
  code?: string;
  name: string;
  category: ItemCategory;
  unit?: Unit;
  selling_price: number;
  purchase_price: number;
  stock_qty?: number;
  min_stock?: number;
  sqft_per_box?: number;
  sqft_per_piece?: number;
  pack_qty?: number | null;
  pack_unit?: string | null;
  weight?: number | null;
  dimensions?: string | null;
  image_path?: string | null;
}

// ─── Estimates ────────────────────────────────────────────────────────────────

export interface EstimateItem {
  id: number;
  estimate_id: number;
  item_id: number | null;
  description: string;
  qty: number;
  unit_price: number; // cents
  discount_type: DiscountType | null;
  discount_value: number;
  discount_amount: number; // cents, computed
  sqft_per_box: number | null;
  sqft: number | null;
  sqft_per_piece: number | null;
  line_total: number; // cents
  sort_order: number;
  note: string | null;
  // Joined from inventory_items in the estimate detail response (GET /api/estimates/:id);
  // not a stored column on estimate_items. Used to reconstruct the sq.ft entry toggle on edit.
  unit?: string | null;
}

export interface Estimate {
  id: number;
  number: string;
  customer_id: number;
  supplier_id: number | null;
  date: string;
  status: EstimateStatus;
  subtotal: number; // cents, sum of line totals
  line_discount_total: number; // cents
  invoice_discount_type: DiscountType | null;
  invoice_discount_value: number;
  invoice_discount_amount: number; // cents, computed
  total: number; // cents, final
  amount_paid: number; // cents
  write_off: number; // cents, small-balance adjustment forgiven off the total
  notes: string | null;
  created_by: number | null;
  created_at: string;
  deleted_at: string | null;
}

// Max amount ($50.00) that can be written off an estimate's or PO's remaining balance.
export const WRITE_OFF_CAP_CENTS = 5000;

// ─── Bulk Write-Off ────────────────────────────────────────────────────────────
// Admin-only tool to write off many small outstanding balances at once (AR
// estimates or AP purchase orders), filtered by document date and a per-order
// max (≤ WRITE_OFF_CAP_CENTS). Reuses applyWriteOff / applyPoWriteOff per order.

export type BulkWriteOffSide = 'ar' | 'ap';

export interface BulkWriteOffOrder {
  id: number;
  number: string;
  party: string; // customer name (AR) / supplier name (AP)
  date: string;
  status: string;
  remaining: number; // cents
}

export interface BulkWriteOffPreviewResponse {
  orders: BulkWriteOffOrder[];
  count: number;
  total_remaining: number; // cents, sum of orders[].remaining
  max_cents_applied: number; // the clamped [1, WRITE_OFF_CAP_CENTS] threshold actually used
}

export interface BulkWriteOffCommitRequest {
  side: BulkWriteOffSide;
  ids: number[];
  max_cents: number;
}

export interface BulkWriteOffCommitResponse {
  written: { id: number; amount: number }[];
  skipped: { id: number; reason: string }[];
  total_written: number; // cents, sum of written[].amount
  count_written: number;
  count_skipped: number;
}

export interface CreateEstimateItemRequest {
  item_id?: number;
  description: string;
  qty: number;
  unit_price: number;
  discount_type?: DiscountType;
  discount_value?: number;
  sqft_per_box?: number;
  sqft?: number;
  sqft_per_piece?: number | null;
  sort_order?: number;
  note?: string | null;
}

export interface CreateEstimateRequest {
  customer_id: number;
  date: string;
  items: CreateEstimateItemRequest[];
  invoice_discount_type?: DiscountType;
  invoice_discount_value?: number;
  notes?: string;
  action: 'draft' | 'issue';
  payment_amount?: number; // initial payment at issue
}

// ─── Payments ─────────────────────────────────────────────────────────────────

export interface Payment {
  id: number;
  estimate_id: number;
  amount: number; // cents
  store_credit_applied: number; // cents
  date: string;
  notes: string | null;
  collected_by: number | null;
  created_at: string;
  deleted_at: string | null;
}

export interface CreatePaymentRequest {
  estimate_id: number;
  amount: number;
  store_credit_applied?: number;
  date: string;
  notes?: string;
}

// ─── Sales Returns ────────────────────────────────────────────────────────────

export interface SalesReturnItem {
  id: number;
  return_id: number;
  estimate_item_id: number | null;
  item_id: number | null;
  description: string;
  qty: number;
  unit_price: number;
  line_total: number;
}

export interface SalesReturn {
  id: number;
  number: string;
  estimate_id: number;
  customer_id: number;
  date: string;
  status: ReturnStatus;
  total: number;
  notes: string | null;
  created_by: number | null;
  created_at: string;
  deleted_at: string | null;
}

// ─── Purchase Orders ─────────────────────────────────────────────────────────

export interface POItem {
  id: number;
  po_id: number;
  item_id: number | null;
  description: string;
  qty: number;
  unit_price: number;
  sqft_per_box: number | null;
  sqft: number | null;
  line_total: number;
  sort_order: number;
}

export interface PurchaseOrder {
  id: number;
  number: string;
  supplier_id: number;
  source_estimate_id: number | null;
  order_date: string;
  expected_date: string | null;
  status: POStatus;
  subtotal: number;
  total: number;
  amount_paid: number;
  write_off: number; // cents, small-balance adjustment forgiven off the total
  notes: string | null;
  created_by: number | null;
  created_at: string;
  deleted_at: string | null;
}

// ─── Purchase Returns ─────────────────────────────────────────────────────────

export interface PurchaseReturn {
  id: number;
  number: string;
  po_id: number;
  supplier_id: number;
  date: string;
  status: ReturnStatus;
  total: number;
  notes: string | null;
  created_by: number | null;
  created_at: string;
  deleted_at: string | null;
}

// ─── Countertop Jobs ──────────────────────────────────────────────────────────

export interface CountertopJob {
  id: number;
  number: string;
  customer_id: number;
  supplier_id: number | null;
  estimate_id: number | null;
  po_id: number | null;
  status: CountertopJobStatus;
  approx_quote: number | null;
  advance_amount: number | null;
  measurement_date: string | null;
  measured_at: string | null;
  install_date: string | null;
  installed_at: string | null;
  site_address: string | null;
  access_detail: string | null;
  material_detail: string | null;
  measurement_area: string | null;
  removal: number | null;
  garbage_disposal: number | null;
  cooktop_cutout: number | null;
  kitchen_sink: 'none' | 'single' | 'double' | null;
  sink_dimensions: string | null;
  edge: 'double' | 'miter' | null;
  notes: string | null;
  approx_sqft: string | null;
  final_sqft: string | null;
  created_by: number | null;
  created_at: string;
  deleted_at: string | null;
}

// ─── Store Credits ────────────────────────────────────────────────────────────

export interface StoreCredit {
  id: number;
  entity_type: EntityType;
  entity_id: number;
  amount: number; // cents; positive = earned, negative = used
  source_type: CreditSourceType;
  source_id: number | null;
  notes: string | null;
  created_by: number | null;
  created_at: string;
}

// ─── Expenses ─────────────────────────────────────────────────────────────────

export interface ExpenseCategory {
  id: number;
  name: string;
  created_at: string;
}

export interface Expense {
  id: number;
  category_id: number;
  date: string;
  amount: number; // cents
  payee: string | null;
  notes: string | null;
  created_by: number | null;
  created_at: string;
  deleted_at: string | null;
}

// ─── Stock Adjustments ────────────────────────────────────────────────────────

export interface StockAdjustment {
  id: number;
  item_id: number;
  direction: StockDirection;
  qty: number;
  reason: StockReason;
  notes: string | null;
  created_by: number | null;
  created_at: string;
}

// ─── Attachments ──────────────────────────────────────────────────────────────

export interface Attachment {
  id: number;
  module: AttachmentModule;
  record_id: number;
  file_path: string;
  original_name: string;
  uploaded_by: number | null;
  created_at: string;
  deleted_at: string | null;
}

// ─── Audit Log ────────────────────────────────────────────────────────────────

export interface AuditEntry {
  id: number;
  user_id: number | null;
  action: string;
  module: string;
  record_id: number | null;
  detail: string | null;
  created_at: string;
}

// ─── Company Settings ─────────────────────────────────────────────────────────

export interface CompanySettings {
  id: 1;
  name: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  logo_path: string | null;
  estimate_terms: string | null;
  po_terms: string | null;
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_user: string | null;
  smtp_pass: string | null;
  smtp_from: string | null;
  updated_at: string | null;
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export interface DashboardStats {
  cash_today: number;
  outstanding_count: number;
  outstanding_total: number;
  low_stock_count: number;
  supplier_payables: number;
  month_revenue: number;
  open_pos_count: number;
  open_pos_total: number;
}
